import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
  Renderer2,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import moment, { Moment, isMoment } from 'moment'
import { DateRange, MatCalendar } from '@angular/material/datepicker'
import {
  CalendarMaxDate,
  EndOFLastWeekString,
  IRangeDateInfo,
  PeriodType,
  PeriodTypeButtonsInfo,
  WeeklyPeriodTypeButtonsInfo,
  MonthlyPeriodTypeButtonsInfo,
  PeriodTypeToLabel,
  TodayString,
  YesterdayString,
  RangePickerType
} from '@lla-platform/core/core-data-access'
import { ISelectItem, ITabsSection } from '@lla-platform/core/core-ui'

@UntilDestroy()
@Component({
  selector: 'lla-range-date-picker',
  templateUrl: './range-date-picker.component.html',
  styleUrls: ['./range-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RangeDatePickerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild(MatCalendar) calendar: MatCalendar<Date>

  @Input() standalone = false
  @Input() dontCheckCalendarView = false
  @Input() value?: IRangeDateInfo
  @Input() maxDate?: CalendarMaxDate
  @Input() minDate?: string
  @Input() hiddenPeriods: PeriodType[] = []
  @Input() hideAllPeriods: boolean
  @Input() showMoonLanding = false
  @Input() focusInput: 'start' | 'end' = 'start'
  @Input() enableRangePickerType = false
  @Input() rangePickerType: RangePickerType = 'daily'

  @Output() valueChanged = new EventEmitter<IRangeDateInfo>()
  @Output() initailValueChanged = new EventEmitter<IRangeDateInfo>()
  @Output() canceld = new EventEmitter()
  @Output() rangePickerTypeChange = new EventEmitter<RangePickerType>()

  calendarMaxDate?: string
  rangeForm: UntypedFormGroup
  periodType = PeriodType
  periodTypeButtonsInfo: ISelectItem[] = []
  periodTypeToLabel = PeriodTypeToLabel

  // Helper method to get all period type buttons (for finding existing values)
  private getAllPeriodTypeButtons(): ISelectItem[] {
    return [
      ...PeriodTypeButtonsInfo(),
      ...WeeklyPeriodTypeButtonsInfo(),
      ...MonthlyPeriodTypeButtonsInfo()
    ]
  }

  // Update period type buttons based on selector type
  private updatePeriodTypeButtons(): void {
    const isHidden = (el: ISelectItem) =>
      this.hiddenPeriods.includes(el.value) ||
      el.value === PeriodType.Custom ||
      (!this.showMoonLanding && el.value === PeriodType.MoonLanding)

    switch (this.rangePickerType) {
      case 'weekly':
        this.periodTypeButtonsInfo = WeeklyPeriodTypeButtonsInfo().filter((el) => !isHidden(el))
        break
      case 'monthly':
        this.periodTypeButtonsInfo = MonthlyPeriodTypeButtonsInfo().filter((el) => !isHidden(el))
        break
      case 'daily':
      default:
        this.periodTypeButtonsInfo = PeriodTypeButtonsInfo().filter((el) => !isHidden(el))
        break
    }
  }

  dateFormat = 'YYYY-MM-DD'
  get validDataFormats(): string[] {
    const baseFormats = [this.dateFormat, 'YYYY-MM-D', 'YYYY-M-DD', 'YYYY-M-D']
    if (this.rangePickerType === 'monthly') {
      // Include monthly formats for monthly mode
      return ['YYYY-MM', 'YYYY-M', ...baseFormats]
    }
    return baseFormats
  }
  selectedDateRange: DateRange<Moment>
  fromControl: AbstractControl | null
  fromTextControl: AbstractControl | null
  toControl: AbstractControl | null
  toTextControl: AbstractControl | null
  private calendarObserver?: MutationObserver

  allCalendarTypes: ITabsSection[] = [
    {
      key: 'daily',
      title: 'Daily'
    },
    {
      key: 'weekly',
      title: 'Weekly'
    },
    {
      key: 'monthly',
      title: 'Monthly'
    }
  ]

  private readonly monthMap: { [key: string]: number } = {
    JAN: 0,
    FEB: 1,
    MAR: 2,
    APR: 3,
    MAY: 4,
    JUN: 5,
    JUL: 6,
    AUG: 7,
    SEP: 8,
    OCT: 9,
    NOV: 10,
    DEC: 11
  }

  // Calendar view properties
  get calendarStartView(): 'month' | 'year' | 'multi-year' {
    return this.rangePickerType === 'monthly' ? 'year' : 'month'
  }

  get dateDisplayFormat(): string {
    return this.rangePickerType === 'monthly' ? 'YYYY-MM' : this.dateFormat
  }

  constructor(
    private fb: UntypedFormBuilder,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    switch (this.maxDate) {
      case 'Today':
        this.calendarMaxDate = TodayString()
        break

      case 'Yesterday':
        this.calendarMaxDate = YesterdayString()
        break

      case 'EndOFLastWeek':
        this.calendarMaxDate = EndOFLastWeekString()
        break

      default:
        this.calendarMaxDate = undefined
        break
    }

    // Initialize period type buttons based on current selector type
    this.updatePeriodTypeButtons()

    if (this.value && this.value.periodType !== PeriodType.Custom) {
      this.value.rangeDate = this.getAllPeriodTypeButtons().find(
        (el) => el.value === this.value?.periodType
      )?.extraInfo
    }

    const { fromInRange, toInRange } = this.getInRangeDates()

    this.rangeForm = this.fb.group({
      from: [fromInRange],
      fromText: [this.transformDate(fromInRange)],
      to: [toInRange],
      toText: [this.transformDate(toInRange)]
    })

    this.fromControl = this.rangeForm.get('from')
    this.fromTextControl = this.rangeForm.get('fromText')
    this.toControl = this.rangeForm.get('to')
    this.toTextControl = this.rangeForm.get('toText')

    this.fromControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.setFromText(value)
    })

    this.toControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.setToText(value)
    })

    this.fromTextControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      let momentDate = moment(value, this.validDataFormats, true)
      if (!value || !momentDate.isValid()) {
        return
      }
      momentDate = momentDate.utcOffset(0, true)

      if (this.rangePickerType === 'weekly') {
        momentDate = this.getStartOfWeek(momentDate)
      } else if (this.rangePickerType === 'monthly') {
        momentDate = this.getStartOfMonth(momentDate)
      }

      if (!momentDate.isAfter(this.calendarMaxDate)) {
        this.fromControl?.setValue(momentDate, { emitEvent: false })
        if (this.value) {
          this.value.periodType = PeriodType.Custom
        } else {
          this.value = {
            periodType: PeriodType.Custom
          }
        }

        this.updateCalendarDataOnly()
        if (this.standalone) {
          this.fireValueChangedEvent()
        }
      }
    })

    this.toTextControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      let momentDate = moment(value, this.validDataFormats, true)
      if (!value || !momentDate.isValid()) {
        return
      }
      momentDate = momentDate.utcOffset(0, true)

      if (this.rangePickerType === 'weekly') {
        momentDate = this.getEndOfWeek(momentDate)
      } else if (this.rangePickerType === 'monthly') {
        momentDate = this.getEndOfMonth(momentDate)
      }

      if (
        !momentDate.isAfter(this.calendarMaxDate) &&
        (!this.fromControl?.value ||
          momentDate.isSame(this.fromControl?.value, 'days') ||
          !momentDate.isBefore(this.fromControl?.value))
      ) {
        this.toControl?.setValue(momentDate, { emitEvent: false })
        if (this.value) {
          this.value.periodType = PeriodType.Custom
        } else {
          this.value = {
            periodType: PeriodType.Custom
          }
        }

        if (this.standalone) {
          this.fireValueChangedEvent()
        }

        this.updateCalendarDataOnly(this.toControl?.value)
      }
    })

    if (this.standalone) {
      this.fireValueChangedEvent(true)
    }
  }

  ngAfterViewInit(): void {
    this.setupWeeklyHoverEvents()
    // Ensure calendar is in correct view after initialization
    setTimeout(() => {
      this.setCorrectCalendarView()
      this.setupCalendarStateListener()
      // Fix incomplete weeks if initially in weekly mode
      if (this.rangePickerType === 'weekly') {
        this.fixIncompleteWeeks()
      }
    }, 100)
  }

  ngOnChanges(): void {
    if (!this.value?.rangeDate) {
      if (this.value) {
        this.value.rangeDate = {
          from: this.calendarMaxDate,
          to: this.calendarMaxDate
        }
      } else {
        this.value = {
          rangeDate: {
            from: this.calendarMaxDate,
            to: this.calendarMaxDate
          }
        }
      }
    }

    const { fromInRange, toInRange } = this.getInRangeDates()

    this.rangeForm?.patchValue(
      {
        from: fromInRange,
        fromText: this.transformDate(fromInRange),
        to: toInRange,
        toText: this.transformDate(toInRange)
      },
      { emitEvent: false }
    )
    this.setCalendarDate()
    // Ensure calendar view is correct after changes
    this.setCorrectCalendarView()
  }

  fireValueChangedEvent(initialChange = false) {
    const from = this.fromControl?.value
    let to = this.toControl?.value

    if (!to) {
      to = from
      this.toControl?.setValue(to)
    }

    const eventPayload = {
      rangeDate: {
        from: isMoment(from) ? from.utc().format() : from,
        to: isMoment(to) ? to.utc().format() : to
      },
      periodType: this.value?.periodType
    }

    if (initialChange) {
      setTimeout(() => {
        this.initailValueChanged.emit(eventPayload)
      })
    } else {
      this.valueChanged.emit(eventPayload)
    }
  }

  setDateByPeriodType(item: ISelectItem) {
    this.focusInput = 'end'
    if (this.value) {
      this.value.periodType = item.value
    } else {
      this.value = {
        periodType: item.value
      }
    }

    this.rangeForm.patchValue({
      from: item.extraInfo.from.isAfter(this.calendarMaxDate)
        ? moment(this.calendarMaxDate).utc()
        : item.extraInfo.from,
      to: item.extraInfo.to.isAfter(this.calendarMaxDate)
        ? moment(this.calendarMaxDate).utc()
        : item.extraInfo.to
    })
    this.setCalendarDate(this.toControl?.value)

    if (this.standalone) {
      this.fireValueChangedEvent()
    }
  }

  transformDate(value?: Moment) {
    return `${value?.format(this.dateDisplayFormat)}`
  }

  onSelectedDateChange(date: Date): void {
    let startMoment = moment(date)
    let endMoment = moment(date)

    if (this.rangePickerType === 'weekly') {
      if (this.focusInput === 'start') {
        startMoment = this.getStartOfWeek(date)
        if (this.selectedDateRange?.end) {
          endMoment = this.selectedDateRange.end
        }
      } else {
        if (this.selectedDateRange?.start) {
          startMoment = this.selectedDateRange.start
        }
        endMoment = this.getEndOfWeek(date)
      }
    } else if (this.rangePickerType === 'monthly') {
      if (this.focusInput === 'start') {
        startMoment = this.getStartOfMonth(date)
        if (this.selectedDateRange?.end) {
          endMoment = this.selectedDateRange.end
        }
      } else {
        if (this.selectedDateRange?.start) {
          startMoment = this.selectedDateRange.start
        }
        endMoment = this.getEndOfMonth(date)
      }
    }

    if (this.focusInput === 'start') {
      if (this.selectedDateRange?.end && startMoment.isBefore(this.selectedDateRange.end)) {
        this.selectedDateRange = new DateRange(startMoment, this.selectedDateRange.end)
      } else {
        this.selectedDateRange = new DateRange(startMoment, null)
      }
      this.focusInput = 'end'
    } else if (this.selectedDateRange.start && endMoment.isSameOrAfter(this.selectedDateRange.start)) {
      this.selectedDateRange = new DateRange(this.selectedDateRange.start, endMoment)
      this.focusInput = 'start'
    }

    this.fromControl?.setValue(this.selectedDateRange.start)
    this.toControl?.setValue(
      this.selectedDateRange.end ? this.selectedDateRange.end : this.selectedDateRange.start
    )

    // Force calendar to update the range display
    if (this.rangePickerType === 'monthly') {
      setTimeout(() => {
        this.setCalendarDate()
        this.updateMonthlyRangeHighlighting()
      }, 10)
    }
    if (this.value) {
      this.value.periodType = PeriodType.Custom
    } else {
      this.value = {
        periodType: PeriodType.Custom
      }
    }
    if (this.standalone) {
      this.fireValueChangedEvent()
    }
  }

  public setCalendarDate(activeDate?: Date) {
    setTimeout(() => {
      this.selectedDateRange = new DateRange(this.fromControl?.value, this.toControl?.value)
      this.setCalendarActiveDate(activeDate ?? this.fromControl?.value)
      // Ensure calendar stays in correct view
      this.setCorrectCalendarView()
      // Add custom range highlighting for monthly mode
      if (this.rangePickerType === 'monthly') {
        setTimeout(() => {
          this.updateMonthlyRangeHighlighting()
        }, 10)
      }
      // Re-setup weekly calendar layout when calendar view changes
      this.setupWeeklyCalendarLayout()
    })
  }

  /**
   * Updates calendar data without triggering DOM operations that might steal focus
   * Used during typing to update calendar while preserving input focus
   */
  public updateCalendarDataOnly(activeDate?: Date): void {
    // Just update the selected date range, don't trigger DOM operations
    this.selectedDateRange = new DateRange(this.fromControl?.value, this.toControl?.value)

    // Only update calendar active date if available, without heavy DOM operations
    if (this.calendar && activeDate) {
      this.calendar.activeDate = activeDate
    } else if (this.calendar && this.fromControl?.value) {
      this.calendar.activeDate = this.fromControl.value
    }
  }

  getInRangeDates() {
    const from = moment(this.value?.rangeDate?.from).utc()
    const to = moment(this.value?.rangeDate?.to).utc()
    const fromInRange = from.isAfter(this.calendarMaxDate) ? moment(this.calendarMaxDate).utc() : from
    const toInRange = to.isAfter(this.calendarMaxDate) ? moment(this.calendarMaxDate).utc() : to

    return { fromInRange, toInRange }
  }

  setCalendarActiveDate(activeDate?: Date) {
    if (this.calendar && activeDate) {
      this.calendar.activeDate = activeDate
      // Trigger range highlighting after setting active date
      if (this.rangePickerType === 'monthly') {
        setTimeout(() => {
          this.updateMonthlyRangeHighlighting()
        }, 10)
      }
    }
  }

  setFromText(value: Moment) {
    if (value) {
      this.fromTextControl?.setValue(this.transformDate(value), {
        emitEvent: false
      })
    }
  }

  setToText(value: Moment) {
    if (value) {
      this.toTextControl?.setValue(this.transformDate(value), {
        emitEvent: false
      })
    }
  }

  getStartOfWeek(date: Date | Moment): Moment {
    const momentDate = isMoment(date) ? date : moment(date)
    const startOfWeek = momentDate.clone().startOf('isoWeek')

    // If start of week is after maxDate, this week is not selectable
    if (this.calendarMaxDate && startOfWeek.isAfter(this.calendarMaxDate)) {
      return moment(this.calendarMaxDate).utc()
    }

    return startOfWeek
  }

  getEndOfWeek(date: Date | Moment): Moment {
    const momentDate = isMoment(date) ? date : moment(date)
    const endOfWeek = momentDate.clone().endOf('isoWeek')

    // If end of week is after maxDate, use maxDate instead
    if (this.calendarMaxDate && endOfWeek.isAfter(this.calendarMaxDate)) {
      return moment(this.calendarMaxDate).utc()
    }

    return endOfWeek
  }

  onSelectorTypeChange(type: any): void {
    this.rangePickerType = type
    this.rangePickerTypeChange.emit(type)

    // Update period type buttons for the new selector type
    this.updatePeriodTypeButtons()

    // Set period type to Custom when calendar type changes
    if (this.value) {
      this.value.periodType = PeriodType.Custom
    } else {
      this.value = {
        periodType: PeriodType.Custom
      }
    }

    // Immediately set the correct calendar view to prevent delay
    this.setCorrectCalendarView()

    // If changing to weekly mode and we have selected dates, adjust them to week boundaries
    if (type === 'weekly' && this.selectedDateRange?.start) {
      const weekStart = this.getStartOfWeek(this.selectedDateRange.start)
      const weekEnd = this.selectedDateRange.end
        ? this.getEndOfWeek(this.selectedDateRange.end)
        : this.getEndOfWeek(this.selectedDateRange.start)

      this.selectedDateRange = new DateRange(weekStart, weekEnd)
      this.fromControl?.setValue(weekStart)
      this.toControl?.setValue(weekEnd)
      this.setCalendarDate()

      if (this.standalone) {
        this.fireValueChangedEvent()
      }
    }

    // If changing to monthly mode and we have selected dates, adjust them to month boundaries
    if (type === 'monthly' && this.selectedDateRange?.start) {
      const monthStart = this.getStartOfMonth(this.selectedDateRange.start)
      const monthEnd = this.selectedDateRange.end
        ? this.getEndOfMonth(this.selectedDateRange.end)
        : this.getEndOfMonth(this.selectedDateRange.start)

      this.selectedDateRange = new DateRange(monthStart, monthEnd)
      this.fromControl?.setValue(monthStart)
      this.toControl?.setValue(monthEnd)
      this.setCalendarDate()

      if (this.standalone) {
        this.fireValueChangedEvent()
      }
    }

    // Update text inputs to reflect the new date format
    this.updateTextInputsFormat()

    // Set appropriate calendar view and setup mode-specific features
    setTimeout(() => {
      // Ensure calendar view is correct after DOM updates
      this.setCorrectCalendarView()
      if (type === 'monthly') {
        this.updateMonthlyRangeHighlighting()
        this.setupMonthClickPrevention()
      }
      this.setupWeeklyCalendarLayout()
    }, 50)
  }

  private updateTextInputsFormat(): void {
    // Update the text inputs with the new format when selector type changes
    if (this.fromControl?.value) {
      this.setFromText(this.fromControl.value)
    }
    if (this.toControl?.value) {
      this.setToText(this.toControl.value)
    }
  }

  setupWeeklyHoverEvents(): void {
    setTimeout(() => {
      this.setupWeeklyCalendarLayout()
      this.setupCalendarObserver()
      this.setupMonthClickPrevention()
    }, 50)
  }

  setupWeeklyCalendarLayout(): void {
    if (this.rangePickerType !== 'weekly') return

    this.fixIncompleteWeeks()
  }

  /**
   * Fixes incomplete weeks by adding td with proper colspan to the last row if needed
   */
  fixIncompleteWeeks(): void {
    if (this.rangePickerType !== 'weekly') return

    const calendarBody = this.elementRef.nativeElement.querySelector('.mat-calendar-body')
    if (!calendarBody) return

    // Get all rows in the calendar body, but exclude label rows
    const allRows = calendarBody.querySelectorAll('tr') as NodeListOf<HTMLTableRowElement>
    const rows = Array.from(allRows).filter((row: HTMLTableRowElement) => {
      // Skip rows that contain only label cells (like month header or spacer rows)
      const hasDateCells = row.querySelector('.mat-calendar-body-cell-container')
      return hasDateCells !== null
    })

    if (rows.length === 0) return

    const lastRow = rows[rows.length - 1]

    // Count actual date cells (not label cells)
    const dateCells = lastRow.querySelectorAll('.mat-calendar-body-cell-container')
    const cellsInLastRow = dateCells.length

    // If the last row doesn't have 7 cells, add filler cells
    if (cellsInLastRow < 7) {
      const missingCells = 7 - cellsInLastRow

      for (let i = 0; i < missingCells; i++) {
        const fillerCell = this.renderer.createElement('td')

        // Add classes to match the styling of other calendar cells
        this.renderer.addClass(fillerCell, 'mat-calendar-body-cell-container')
        this.renderer.addClass(fillerCell, 'weekly-filler-cell')

        // Set role attribute
        this.renderer.setAttribute(fillerCell, 'role', 'gridcell')

        // Set empty content to maintain layout
        this.renderer.setProperty(fillerCell, 'innerHTML', '&nbsp;')

        // Append to the last row
        this.renderer.appendChild(lastRow, fillerCell)
      }
    }
  }

  setupCalendarObserver(): void {
    const calendarElement = this.elementRef.nativeElement.querySelector('.mat-calendar-body')
    if (!calendarElement) return

    // Disconnect existing observer if any
    if (this.calendarObserver) {
      this.calendarObserver.disconnect()
    }

    // Create a new observer to watch for calendar DOM changes
    this.calendarObserver = new MutationObserver((mutations) => {
      const hasChildListChanges = mutations.some((mutation) => mutation.type === 'childList')
      if (hasChildListChanges) {
        setTimeout(() => {
          // Re-apply monthly range highlighting and click prevention if in monthly mode
          if (this.rangePickerType === 'monthly') {
            this.updateMonthlyRangeHighlighting()
            this.setupMonthClickPrevention()
          }
          if (this.rangePickerType === 'weekly') {
            this.setupWeeklyCalendarLayout()
          }
        }, 50)
      }
    })

    // Start observing calendar changes
    this.calendarObserver.observe(calendarElement, {
      childList: true,
      subtree: true
    })
  }

  onYearSelected(year: Moment): void {
    // Year selection - just update the calendar view, don't select dates
    if (this.rangePickerType === 'monthly') {
      // For monthly mode, we'll let the user select months from the year view
      return
    }
  }

  onViewChanged(view: 'month' | 'year' | 'multi-year'): void {
    // In monthly mode, prevent the calendar from going to 'month' view (showing days)
    if (this.rangePickerType === 'monthly' && view === 'month') {
      // Immediately force back to year view to prevent flicker
      if (this.calendar) {
        this.calendar.currentView = 'year'
      }
    }
  }

  setCorrectCalendarView(): void {
    if (this.dontCheckCalendarView) {
      return
    }
    if (this.calendar) {
      if (this.rangePickerType === 'monthly') {
        this.calendar.currentView = 'year'
      } else {
        this.calendar.currentView = 'month'
      }
    }
  }

  getStartOfMonth(date: Date | Moment): Moment {
    const momentDate = isMoment(date) ? date : moment(date)
    const startOfMonth = momentDate.clone().utc().startOf('month')

    // If start of month is after maxDate, this month is not selectable
    if (this.calendarMaxDate && startOfMonth.isAfter(this.calendarMaxDate)) {
      return moment(this.calendarMaxDate).utc()
    }

    return startOfMonth
  }

  getEndOfMonth(date: Date | Moment): Moment {
    const momentDate = isMoment(date) ? date : moment(date)
    const endOfMonth = momentDate.clone().utc().endOf('month')

    // If end of month is after maxDate, use maxDate instead
    if (this.calendarMaxDate && endOfMonth.isAfter(this.calendarMaxDate)) {
      return moment(this.calendarMaxDate).utc()
    }

    return endOfMonth
  }

  updateMonthlyRangeHighlighting(): void {
    if (this.rangePickerType !== 'monthly' || !this.fromControl?.value || !this.toControl?.value) {
      return
    }

    const calendarElement = this.elementRef.nativeElement.querySelector('.mat-calendar-body')
    if (!calendarElement) return

    // Clear existing custom highlighting
    const cells = calendarElement.querySelectorAll('.mat-calendar-body-cell')
    cells.forEach((cell: Element) => {
      this.renderer.removeClass(cell, 'custom-range-start')
      this.renderer.removeClass(cell, 'custom-range-end')
      this.renderer.removeClass(cell, 'custom-range-middle')
    })

    const startMonth = moment(this.fromControl.value)
    const endMonth = moment(this.toControl.value)

    // Get the current year being displayed in the calendar
    const currentCalendarYear = this.calendar?.activeDate
      ? moment(this.calendar.activeDate).year()
      : moment.utc().year()

    // Get all month cells and apply custom highlighting
    cells.forEach((cell: Element) => {
      const cellText = cell.textContent?.trim()
      if (!cellText) return

      const cellMonthIndex = this.monthMap[cellText.toUpperCase()]
      if (cellMonthIndex === undefined) return

      // Check if this cell month should be highlighted based on the current calendar year
      const isStartMonthInCurrentYear =
        startMonth.year() === currentCalendarYear && cellMonthIndex === startMonth.month()
      const isEndMonthInCurrentYear =
        endMonth.year() === currentCalendarYear && cellMonthIndex === endMonth.month()

      // For cross-year selections, determine if this month should be highlighted
      const isInRange = this.isMonthInRange(cellMonthIndex, currentCalendarYear, startMonth, endMonth)

      if (isStartMonthInCurrentYear && isEndMonthInCurrentYear) {
        // Same month and year for start and end
        this.renderer.addClass(cell, 'custom-range-start')
        this.renderer.addClass(cell, 'custom-range-end')
      } else if (isStartMonthInCurrentYear) {
        // Start month in current year
        this.renderer.addClass(cell, 'custom-range-start')
      } else if (isEndMonthInCurrentYear) {
        // End month in current year
        this.renderer.addClass(cell, 'custom-range-end')
      } else if (isInRange) {
        // Middle months
        this.renderer.addClass(cell, 'custom-range-middle')
      }
    })
  }

  private isMonthInRange(
    cellMonthIndex: number,
    currentYear: number,
    startMonth: moment.Moment,
    endMonth: moment.Moment
  ): boolean {
    const cellDate = moment.utc().year(currentYear).month(cellMonthIndex).startOf('month')
    const startDate = startMonth.clone().utc().startOf('month')
    const endDate = endMonth.clone().utc().startOf('month')

    return cellDate.isAfter(startDate) && cellDate.isBefore(endDate)
  }

  setupCalendarStateListener(): void {
    if (this.calendar) {
      // Listen to calendar state changes (like month/year navigation)
      this.calendar.stateChanges.pipe(untilDestroyed(this)).subscribe(() => {
        setTimeout(() => {
          if (this.rangePickerType === 'monthly') {
            this.updateMonthlyRangeHighlighting()
            this.setupMonthClickPrevention()
          }

          if (this.rangePickerType === 'weekly') {
            this.setupWeeklyCalendarLayout()
          }
        }, 50)
      })
    }
  }

  setupMonthClickPrevention(): void {
    if (this.rangePickerType !== 'monthly') return

    const calendarElement = this.elementRef.nativeElement.querySelector('.mat-calendar-body')
    if (!calendarElement) return

    // Add capturing event listeners to month cells to intercept before Material Calendar
    const monthCells = calendarElement.querySelectorAll('.mat-calendar-body-cell')
    monthCells.forEach((cell: Element) => {
      // Remove any existing listeners first (though not necessary with new DOM elements)
      cell.removeEventListener('click', this.monthClickHandler, { capture: true })

      // Use addEventListener with capture: true to fire before Material Calendar's handlers
      cell.addEventListener('click', this.monthClickHandler, { capture: true })
    })
  }

  private monthClickHandler = (event: Event) => {
    if (this.rangePickerType === 'monthly') {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      // Get the month from the cell text and handle selection manually
      const cellText = (event.target as Element)?.textContent?.trim()
      if (cellText) {
        this.handleManualMonthSelection(cellText)
      }
    }
  }

  private handleManualMonthSelection(monthText: string): void {
    const monthIndex = this.monthMap[monthText.toUpperCase()]
    if (monthIndex !== undefined && this.calendar) {
      const currentYear = this.calendar.activeDate
        ? moment(this.calendar.activeDate).year()
        : moment.utc().year()
      const selectedMonth = moment.utc().year(currentYear).month(monthIndex).startOf('month')

      // Handle the month selection without triggering calendar view change
      this.onSelectedDateChange(selectedMonth.toDate())

      // Ensure we stay in year view
      this.calendar.currentView = 'year'
    }
  }

  ngOnDestroy(): void {
    // Clean up mutation observer
    if (this.calendarObserver) {
      this.calendarObserver.disconnect()
    }
  }
}
